import rootStore from '@vue-storefront/core/store'
import { isServer, processURLAddress } from '@vue-storefront/core/helpers'
import { customerAuthHeaders, isDefinitiveRefreshFailure } from '@vue-storefront/core/modules/user/helpers/customerSession'

var fetch = require('isomorphic-fetch')
let customerCartToken

function currentToken () {
  return rootStore.getters['user/getUserToken'] ? rootStore.getters['user/getUserToken'] : rootStore.state.user.token
}

function currentRefreshToken () {
  return rootStore.getters['user/getRefreshToken'] ? rootStore.getters['user/getRefreshToken'] : rootStore.state.user.refreshToken
}

async function logoutAfterRejectedRefresh () {
  if (isServer) {
    rootStore.state.logoutUser = true
  }
  await rootStore.dispatch('user/logout', { silent: true })
  return rootStore.dispatch('cart/connect')
}

/**
 * A 401: refresh once (shared with every other caller) and retry. Only a rejected refresh token
 * logs out, then the request is retried as a guest; a 5xx/network failure keeps the session (K2-3138).
 */
function recoverFromUnauthorized (token, retry, giveUp) {
  // the browser's refresh token is the HttpOnly rut cookie, invisible here; an access token means a session
  let canRefresh = isServer ? !!currentRefreshToken() : !!token
  if (!canRefresh) {
    if (!token && !currentRefreshToken()) {
      if (isServer) {
        rootStore.state.logoutUser = true
      }
      rootStore.dispatch('user/logout', { silent: true })
    }
    return giveUp()
  }
  // the retry's own outcome (or error) is the answer; only a refresh failure is handled here
  return Promise.resolve(rootStore.dispatch('user/refresh', { failedToken: token }))
    .then(() => retry(), async err => {
      if (!isDefinitiveRefreshFailure(err)) {
        return giveUp()
      }
      await logoutAfterRejectedRefresh()
      return retry()
    })
}

function buildApiUrl (baseUrl, params, processParams = false) {
  let url = processURLAddress(baseUrl)
  // K2-3138: the token goes as a Bearer header; an old {{token}} placeholder is left empty
  url = url.replace('{{token}}', '')
    .replace('{{cartId}}', !customerCartToken ? '' : customerCartToken)

  let keys = url.match(/({{)(\w*)(}})/g)
  if (keys) {
    keys.forEach(key => {
      url = url.replace(key, params[key.replace(/{|}/g, '')])
    })
  }

  if (processParams) {
    // '?' unless the URL has a query already (it no longer always starts with ?token=, K2-3138)
    Object.keys(params).forEach(key => {
      url = `${url}${url.includes('?') ? '&' : '?'}${key}=${params[key]}`
    })
  }

  return url
}

function send (url, method, params, retried) {
  let originalUrl = url
  customerCartToken = !rootStore.getters['cart/getCartToken'] ? rootStore.state.cart.cartServerToken : rootStore.getters['cart/getCartToken']
  const token = currentToken()
  url = buildApiUrl(url, params, method === 'GET')
  let requestOptions = {
    method,
    mode: 'cors',
    headers: Object.assign({
      'Accept': 'application/json, text/plain, */*',
      'Content-Type': 'application/json'
    }, customerAuthHeaders(token))
  }
  if (method !== 'GET') {
    requestOptions.body = JSON.stringify(params)
  }
  const retry = () => send(originalUrl, method, params, true)
  return fetch(processURLAddress(url), requestOptions)
    .then(async res => {
      if (res.ok && res.status !== 204) return res.json();
      if (!res.ok) {
        let err = {}
        let data = await res.json()
        err.message = data.message
        if (data.errors) {
          err.errors = data.errors
        }
        err.code = res.status
        throw err
      }
      return res
    })
    // one handler pair: a failed retry must not start another refresh
    .then(resp => {
      if (resp.code === 401 && !retried) {
        return recoverFromUnauthorized(token, retry, () => resp)
      }
      return resp
    }, err => {
      if (err.code === 401 && !retried) {
        return recoverFromUnauthorized(token, retry, () => { throw err })
      }
      throw err
    })
}

export function request (url, method = 'GET', params = {}) {
  return send(url, method, params, false)
}
